import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { STREAM } from '@constants/timing';
import { API_BASE, AUTH_EXPIRED_EVENT, ME_PATH } from '@lib/api';
import { StreamContext, type StreamHandler, type StreamStats } from './context';
import { SSE_TYPES, type StreamType } from './payloads';

/** One EventSource for the whole app; pages subscribe to the message types they care about. */
export function StreamProvider({ children }: { children: ReactNode }) {
  const [connected, setConnected] = useState(false);
  const handlers = useRef(new Set<StreamHandler>());
  const statsRef = useRef<StreamStats & { gatewayAt: Map<string, number> }>({ openedAt: null, lastMessageAt: null, gatewayAt: new Map() });

  useEffect(() => {
    let es: EventSource | null = null;
    let retry: number | undefined;
    let stopped = false;
    let opened = false;
    let attempt = 0;
    const dispatch = (type: StreamType, data: unknown) =>
      handlers.current.forEach((h) => {
        try {
          h(type, data);
        } catch (err) {
          // one broken subscriber must not stop the others
          console.error('stream handler failed', err);
        }
      });
    // A proxy or a mobile network can keep a dead connection looking open; silence for this long means
    // the stream is gone, so reconnect (and reload).
    let lastMessageAt = Date.now();
    const stats = statsRef.current;
    const heard = () => {
      lastMessageAt = Date.now();
      stats.lastMessageAt = lastMessageAt;
    };
    /** a gateway is alive: its reads or its heartbeat/status came through */
    const fromGateway = (t: StreamType, data: unknown) => {
      if (t === 'events') {
        const m = data as { gw?: string; events?: { gwId?: string }[] };
        if (m.gw) stats.gatewayAt.set(m.gw, lastMessageAt);
        for (const e of m.events ?? []) if (e.gwId) stats.gatewayAt.set(e.gwId, lastMessageAt);
      } else if (t === 'gateway') {
        const id = (data as { gwId?: string }).gwId;
        if (id) stats.gatewayAt.set(id, lastMessageAt);
      }
    };
    const watchdog = window.setInterval(() => {
      if (stopped || es?.readyState !== EventSource.OPEN || Date.now() - lastMessageAt < STREAM.silenceMs) return;
      es.close();
      setConnected(false);
      reconnectLater();
    }, STREAM.watchdogMs);
    const reconnectLater = () => {
      const delay = Math.min(STREAM.reconnectMaxMs, STREAM.reconnectBaseMs * 2 ** attempt++);
      retry = window.setTimeout(async () => {
        if (stopped) return;
        try {
          const r = await fetch(`${API_BASE}${ME_PATH}`, { credentials: 'same-origin' });
          if (r.status === 401) {
            window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
            return;
          }
        } catch {
          /* offline: try the stream again anyway */
        }
        if (!stopped) open();
      }, delay);
    };
    const open = () => {
      es = new EventSource(`${API_BASE}/stream`);
      lastMessageAt = Date.now();
      es.onopen = () => {
        heard();
        stats.openedAt ??= lastMessageAt;
        setConnected(true);
        attempt = 0;
        if (opened) dispatch('reconnect', null);
        opened = true;
      };
      es.onerror = () => {
        setConnected(false);
        // the browser retries by itself while CONNECTING; a CLOSED stream (e.g. 401, server gone) is ours to retry
        if (es?.readyState !== EventSource.CLOSED || stopped) return;
        es.close();
        reconnectLater();
      };
      es.addEventListener('ping', heard);
      for (const t of SSE_TYPES) {
        es.addEventListener(t, (m) => {
          heard();
          let data: unknown;
          try {
            data = JSON.parse((m as MessageEvent).data);
          } catch {
            return;
          }
          fromGateway(t, data);
          dispatch(t, data);
        });
      }
    };
    const onVisible = () => document.visibilityState === 'visible' && opened && dispatch('reconnect', null);
    document.addEventListener('visibilitychange', onVisible);
    open();
    return () => {
      stopped = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(watchdog);
      window.clearTimeout(retry);
      es?.close();
    };
  }, []);

  const subscribe = useCallback((h: StreamHandler) => {
    handlers.current.add(h);
    return () => {
      handlers.current.delete(h);
    };
  }, []);
  const stats = useCallback(() => statsRef.current, []);
  const value = useMemo(() => ({ connected, subscribe, stats }), [connected, subscribe, stats]);
  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
}
